<template>
  <v-dialog v-model="internalValue" max-width="600" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">Set Subscription Status</span>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="device" class="mb-4">
          <div class="text-subtitle-1 font-weight-medium">
            {{ device.customName || 'Unnamed Device' }}
          </div>
          <div class="text-caption grey--text">
            ID: {{ device.id }} | Company: {{ device.companyName }}
          </div>
          <div class="text-caption grey--text">
            Current Status:
            <v-chip :color="getStatusColor(device.status)" text-color="white" x-small class="ml-1">
              {{ device.status }}
            </v-chip>
          </div>
        </div>

        <v-form ref="form" v-model="formValid" @submit.prevent="setStatus">
          <!-- Status Selection -->
          <v-select
            v-model="form.status"
            :items="statusOptions"
            label="Target Status *"
            required
            :rules="[(v) => !!v || 'Status is required']"
            hint="Select the desired subscription status"
            persistent-hint
            class="mb-4"
          />

          <!-- Custom Days Input -->
          <v-text-field
            v-model="form.daysFromNow"
            label="Custom Days from Now"
            type="number"
            hint="Be careful! This change subscription row in database. Use with caution."
            persistent-hint
            class="mb-4"
          />

          <!-- Custom Minutes Input (for immediate testing) -->
          <v-text-field
            v-model="form.minutesFromNow"
            label="Custom Minutes from Now"
            type="number"
            hint="For immediate expiration testing. Overrides days setting."
            persistent-hint
            class="mb-4"
          />

          <!-- Quick Notification Presets -->
          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">Quick Notification Test Presets:</div>

            <!-- Days Presets -->
            <div class="mb-2">
              <div class="text-caption mb-1">Days:</div>
              <div class="d-flex flex-wrap gap-2">
                <v-btn small color="info" @click="setNotificationDay(14)"> 14 Days </v-btn>
                <v-btn small color="warning" @click="setNotificationDay(7)"> 7 Days </v-btn>
                <v-btn small color="orange" @click="setNotificationDay(3)"> 3 Days </v-btn>
                <v-btn small color="error" @click="setNotificationDay(1)"> 1 Day </v-btn>
              </div>
            </div>

            <!-- Minutes Presets -->
            <div class="mb-2">
              <div class="text-caption mb-1">Minutes (immediate expiration testing):</div>
              <div class="d-flex flex-wrap gap-2">
                <v-btn small color="red darken-2" @click="setNotificationMinutes(30)">
                  <v-icon left small>mdi-clock-fast</v-icon>
                  30 Minutes
                </v-btn>
                <v-btn small color="red darken-3" @click="setNotificationMinutes(10)">
                  <v-icon left small>mdi-clock-alert</v-icon>
                  10 Minutes
                </v-btn>
                <v-btn small color="red darken-4" @click="setNotificationMinutes(5)">
                  <v-icon left small>mdi-timer</v-icon>
                  5 Minutes
                </v-btn>
                <v-btn small color="red darken-4" @click="setNotificationMinutes(1)">
                  <v-icon left small>mdi-timer-alert</v-icon>
                  1 Minute
                </v-btn>
              </div>
            </div>

            <div class="text-caption grey--text mt-1">
              Click to set device to expire in exactly these timeframes (for notification testing)
            </div>
          </div>

          <!-- Status Explanations -->
          <v-expansion-panels flat class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <div class="text-subtitle-2">How this works</div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="text-body-2">
                  <li><strong>VALID:</strong> Creates subscription expiring 30+ days in future</li>
                  <li>
                    <strong>SOON_EXPIRING:</strong> Creates subscription expiring within 14 days
                  </li>
                  <li><strong>EXPIRED:</strong> Creates subscription that expired in the past</li>
                  <li>
                    <strong>UNPAID:</strong> Marks subscription as unpaid (expired 1 day ago by
                    default)
                  </li>
                  <li>
                    <strong>NOT_DEFINED:</strong> Removes subscriptions and device activation date
                  </li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close"> Cancel </v-btn>
        <v-btn color="primary" :loading="loading" :disabled="!formValid" @click="setStatus">
          Set Status
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { adminSetSubscriptionStatus } from '@/graphql/mutations/adminSetSubscriptionStatus'

export default {
  name: 'SetSubscriptionStatusModal',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    device: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      formValid: false,
      loading: false,
      form: {
        status: null,
        daysFromNow: null,
        minutesFromNow: null,
      },

      statusOptions: [
        { text: 'Valid (Active subscription, 30+ days remaining)', value: 'VALID' },
        { text: 'Soon Expiring (Expires within 14 days)', value: 'SOON_EXPIRING' },
        { text: 'Expired (Already expired)', value: 'EXPIRED' },
        { text: 'Unpaid (Payment not received)', value: 'UNPAID' },
        { text: 'Blocked (SIM card has been deactivated)', value: 'BLOCKED' },
        {
          text: 'Reactivation pending (Payed, waiting for reactivation)',
          value: 'REACTIVATION_PENDING',
        },
        { text: 'Not Defined (No subscription)', value: 'NOT_DEFINED' },
      ],
    }
  },

  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.resetForm()
      }
    },
    'form.status'(newStatus) {
      this.form.daysFromNow = this.getDefaultDaysForStatus(newStatus)
    },
  },

  methods: {
    close() {
      this.internalValue = false
    },

    getDefaultDaysForStatus(status) {
      switch (status) {
        case 'VALID':
        case 'REACTIVATION_PENDING':
          return 365
        case 'SOON_EXPIRING':
          return 7 // 7 days from now (within 14-day threshold)
        case 'EXPIRED':
          return -10 // 10 days ago
        case 'UNPAID':
          return -30
        case 'BLOCKED':
          return null
        default:
          return null
      }
    },

    resetForm() {
      this.form = {
        status: null,
        daysFromNow: null,
        minutesFromNow: null,
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },

    setNotificationDay(days) {
      this.form.status = 'SOON_EXPIRING'
      this.form.daysFromNow = days
      this.form.minutesFromNow = null // Clear minutes when setting days
    },

    setNotificationMinutes(minutes) {
      this.form.status = 'SOON_EXPIRING'
      this.form.minutesFromNow = minutes
      this.form.daysFromNow = null // Clear days when setting minutes
    },

    getStatusColor(status) {
      switch (status) {
        case 'VALID':
          return 'success'
        case 'SOON_EXPIRING':
          return 'warning'
        case 'EXPIRED':
          return 'error'
        case 'UNPAID':
          return 'error'
        case 'BLOCKED':
          return 'error'
        case 'REACTIVATION_PENDING':
          return 'warning'
        case 'NOT_DEFINED':
          return 'grey'
        default:
          return 'grey'
      }
    },

    async setStatus() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true

      try {
        const input = {
          deviceId: this.device.id,
          status: this.form.status,
        }

        // Minutes take priority over days
        if (this.form.minutesFromNow !== null && this.form.minutesFromNow !== '') {
          input.minutesFromNow = parseInt(this.form.minutesFromNow)
        } else if (this.form.daysFromNow !== null && this.form.daysFromNow !== '') {
          input.daysFromNow = parseInt(this.form.daysFromNow)
        }

        const { data } = await this.$apollo.mutate({
          mutation: adminSetSubscriptionStatus,
          variables: { input },
        })

        if (data.adminSetSubscriptionStatus.success) {
          this.$toast.success(
            `Subscription status updated successfully for ${data.adminSetSubscriptionStatus.deviceName}`,
          )
          this.$emit('success')
        } else {
          this.$toast.error('Failed to update subscription status')
        }
      } catch (error) {
        console.error('Error setting subscription status:', error)
        this.$toast.error('Error setting subscription status: ' + error.message)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.gap-2 > * + * {
  margin-left: 8px;
}
</style>
